import { authService } from '@/core/sso/oidc/AuthService'
import { ServicePermissions } from '@/enums/ServicePermissions'
import AppLayout from '@/layout/AppLayout.vue'
import { PermissionChecker } from '@/service/permissions/PermissionChecker'
import Error from '@/views/pages/Error.vue'
import NotFound from '@/views/pages/NotFound.vue'
import Unauthorized from '@/views/pages/Unauthorized.vue'
import { createRouter, createWebHistory } from 'vue-router'

const permissionChecker = new PermissionChecker()

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      component: AppLayout,
      meta: {
        requiresAuth: true
      },
      children: [
        {
          path: '/',
          name: 'Dashboard',
          component: () => import('@/views/pages/DashboardGeneral.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.DashboardPermission]
          }
        },
        {
          path: '/chargingsites',
          name: 'Sites monitoring',
          component: () => import('@/views/pages/Site/SitesOverview.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.SiteMonitoringPermission]
          }
        },
        {
          path: '/sitedetails/:siteId',
          name: 'sitedetails',
          component: () => import('@/views/pages/Site/SiteDetails.vue'),
          props: true,
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.SiteDetailsPermission]
          }
        },
        {
          path: '/localcontrollers',
          name: 'localcontrollers',
          component: () => import('@/views/pages/LocalControllersOverview.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.LocalControllerPermission]
          }
        },
        {
          path: '/reporting',
          name: 'reporting',
          component: () => import('@/views/pages/Reporting.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.ReportingPermission]
          }
        },
        {
          path: '/localcontroller/:serialNumber',
          name: 'localcontroller',
          component: () => import('@/views/pages/LocalControllerDetails.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.LocalControllerDetailsPermission]
          }
        },
        {
          path: '/chargepoints',
          name: 'chargepoints',
          component: () => import('@/views/pages/ChargePoints/ChargePointsOverview.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.ChargePointsPermission]
          }
        },
        {
          path: '/chargepointdetails/:chargePointId',
          name: 'chargepointdetails',
          props: true,
          component: () => import('@/views/pages/ChargePoints/ChargePointDetails.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.ChargePointDetailsPermission]
          }
        },
        {
          path: '/casemanagement',
          name: 'casemanagement',
          component: () => import('@/views/pages/CaseManagement/CaseManagementOverview.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.CaseManagementPermission]
          }
        },
        {
          path: '/casemanagement/:caseNumber',
          name: 'casemanagementdetails',
          props: true,
          component: () => import('@/views/pages/CaseManagement/CaseManagementDetails.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.CaseManagementDetailsPermission]
          }
        },
        {
          path: '/casemanagement/create',
          name: 'casemanagementcreate',
          component: () => import('@/views/pages/CaseManagement/Create/CaseManagementCreate.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.CaseManagementCreatePermission]
          }
        },
        {
          path: '/authorisations',
          name: 'authorisations',
          component: () => import('@/views/pages/Authorisations/AuthorisationsOverview.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.AuthorisationsPermission]
          }
        },
        {
          path: '/authorisationdetails/:authorisationId',
          name: 'authorisationdetails',
          props: true,
          component: () => import('@/views/pages/Authorisations/AuthorisationDetails.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.AuthorisationDetailsPermission]
          }
        },
        {
          path: '/loyalty',
          name: 'loyalty',
          component: () => import('@/views/pages/Loyalty/LoyaltyUsers.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.LoyaltyUsersPermission]
          }
        },
        {
          path: '/loyalty/:userId',
          name: 'loyaltyuserdetails',
          component: () => import('@/views/pages/Loyalty/LoyaltyUserDetails.vue'),
          props: true,
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.LoyaltyUserDetailsPermission]
          }
        },
        {
          path: '/chargingsessions',
          name: 'chargingsessions',
          component: () => import('@/views/pages/Sessions/SessionsOverview.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.SessionPermission]
          }
        },
        {
          path: '/sessiondetails/:sessionId',
          name: 'sessiondetails',
          component: () => import('@/views/pages/Sessions/SessionDetails.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.SessionDetailsPermission]
          }
        },
        {
          path: '/workorders',
          name: 'workorders',
          component: () => import('@/views/pages/WorkOrder/WorkOrderOverview.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.WorkOrderPermission]
          }
        },
        {
          path: '/firmwaremanagement',
          name: 'firmwaremanagement',
          component: () => import('@/views/pages/Firmware/FirmwaresOverview.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.FirmwarePermission]
          }
        },
        {
          path: '/firmwaremanagement/:firmwareId',
          name: 'firmwareDetails',
          component: () => import('@/views/pages/Firmware/FirmwareDetails.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.FirmwarePermissionDetails]
          }
        },
        {
          path: '/logs',
          name: 'logs',
          component: () => import('@/views/pages/ActionLogs/LogsOverview.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.LogsPermission]
          }
        },
        {
          path: '/users',
          name: 'users',
          component: () => import('@/views/pages/UserManagement/UsersOverview.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.ServiceUserManagementUsersView]
          }
        },
        {
          path: '/userdetails/:userId',
          name: 'userdetails',
          component: () => import('@/views/pages/UserManagement/UserDetails.vue'),
          props: true,
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.UserManagementUserDetailsPermission]
          }
        },
        {
          path: '/useradd/',
          name: 'useradd',
          component: () => import('@/views/pages/UserManagement/UserAdd.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.UserManagementUserAddPermission]
          }
        },
        {
          path: '/useredit/:userId/',
          name: 'useredit',
          component: () => import('@/views/pages/UserManagement/UserEdit.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.UserManagementUserEditPermission]
          }
        },
        {
          path: '/userduplicate/:userId/',
          name: 'userduplicate',
          component: () => import('@/views/pages/UserManagement/UserDuplicate.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.UserManagementUserDuplicatePermission]
          }
        },
        {
          path: '/permissions',
          name: 'permissions',
          component: () => import('@/views/pages/UserManagement/PermissionsOverview.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.ServiceUserManagementPermissionsView]
          }
        },
        {
          path: '/permissiondetails/:permissionId',
          name: 'permissiondetails',
          component: () => import('@/views/pages/UserManagement/PermissionDetails.vue'),
          props: true,
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.UserManagementPermissionDetailsPermission]
          }
        },
        {
          path: '/permissionadd',
          name: 'permissionadd',
          component: () => import('@/views/pages/UserManagement/PermissionAdd.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.UserManagementPermissionAddPermission]
          }
        },
        {
          path: '/permissionedit/:permissionId',
          name: 'permissionedit',
          component: () => import('@/views/pages/UserManagement/PermissionEdit.vue'),
          props: true,
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.UserManagementPermissionEditPermission]
          }
        },
        {
          path: '/groups',
          name: 'groups',
          component: () => import('@/views/pages/UserManagement/GroupsOverview.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.ServiceUserManagementGroupsView]
          }
        },
        {
          path: '/groupdetails/:groupId',
          name: 'groupdetails',
          component: () => import('@/views/pages/UserManagement/GroupDetails.vue'),
          props: true,
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.UserManagementGroupDetailsPermission]
          }
        },
        {
          path: '/roles',
          name: 'roles',
          component: () => import('@/views/pages/UserManagement/RolesOverview.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.ServiceUserManagementRolesView]
          }
        },
        {
          path: '/roleadd',
          name: 'roleadd',
          component: () => import('@/views/pages/UserManagement/RoleAdd.vue'),
          props: true,
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.UserManagementRoleAddPermission]
          }
        },
        {
          path: '/roledetails/:roleId',
          name: 'roledetails',
          component: () => import('@/views/pages/UserManagement/RoleDetails.vue'),
          props: true,
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.UserManagementRoleDetailsPermission]
          }
        },
        {
          path: '/roleedit/:roleId/',
          name: 'roleedit',
          component: () => import('@/views/pages/UserManagement/RoleEdit.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.UserManagementRoleEditPermission]
          }
        },
        {
          path: '/organisations',
          name: 'organisations',
          component: () => import('@/views/pages/UserManagement/OrganisationsOverview.vue'),
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.ServiceUserManagementOrganisationsView]
          }
        },
        {
          path: '/organisationdetails/:organisationId',
          name: 'organisationdetails',
          component: () => import('@/views/pages/UserManagement/OrganisationDetails.vue'),
          props: true,
          meta: {
            requiresAuth: true,
            servicePermissions: [ServicePermissions.UserManagementOrganisationDetailsPermission]
          }
        },
        {
          path: '/signin-oidc',
          component: () => import('@/components/Authentication/CallbackComponent.vue'),
          meta: {
            requiresAuth: false
          }
        },
        {
          path: '/login',
          component: () => import('@/components/Authentication/LoginComponent.vue'),
          meta: {
            requiresAuth: false
          }
        },
        {
          path: '/profile',
          name: 'Profile',
          component: () => import('@/views/pages/UserPreferences.vue'),
          meta: {
            requiresAuth: true
          }
        },
        {
          path: '/unauthorized',
          component: Unauthorized,
          meta: {
            requiresAuth: false
          }
        },
        {
          path: '/error',
          component: Error,
          meta: {
            requiresAuth: false
          }
        },
        {
          path: '/:pathMatch(.*)*',
          component: NotFound,
          meta: {
            requiresAuth: false
          }
        }
      ]
    }
  ],
  scrollBehavior() {
    return {
      top: 0,
      behavior: 'smooth'
    }
  }
})

const checkPermissions = (permissions: ServicePermissions[] | null | undefined): boolean => {
  if (permissions) {
    return permissionChecker.canGo(permissions)
  }
  return true
}

router.beforeEach(async (to, _from, next) => {
  if (import.meta.env.MODE === 'ci') {
    next()
  }

  if (to.meta.requiresAuth) {
    try {
      const isAuthenticated = await authService.checkAuthentication()
      if (!isAuthenticated) {
        next(`/login?redirect=${to.fullPath}`)
      } else {
        if (checkPermissions(to.meta.servicePermissions as ServicePermissions[])) {
          next()
        } else {
          next('/unauthorized')
        }
      }
    } catch (error) {
      next(`/login?redirect=${to.fullPath}`)
    }
  } else {
    next()
  }
})

export default router
